import React, { FC, MutableRefObject, ReactElement, useCallback, useContext, useMemo, useState } from 'react'
import useIsMountedRef from 'use-is-mounted-ref'
import { List } from '@material-ui/core'
import { useHotkeys } from 'react-hotkeys-hook'
import { UserMenu as UserMenuLanguage } from '../../../../../../i18n'
import CLanguageHandler from '../../../../../NonReact/Languages/LanguageHandler'
import { LanguageContext, ReactState } from '../../../../../React/Common/Contexts'
import { IconName } from '../../../../../React/Common/Components/Icon'
import { signOut } from '../../../../../React/Common/API/UserSignInOutAPI'
import { UserBadge } from '../../../../../React/Common/Components/UserBadge/UserBadge'
import { EMPTY_STRING } from '../../../../../NonReact/constants/Common'
import { Branding } from '../../../../../React/Common/API/BrandingAPI'
import { useBranding } from '../../../../../React/Common/Hooks/useBranding'
import { openHelp } from '../../../../../React/Common/Utils/HelpPortal'
import { moveToURL } from '../../../../../NonReact/constants/LocationUtils'
import {
    cookiePolicyURL,
    getAuthURL,
    getChangeLanguageURL,
    getChangePasswordURL,
    privacyPolicyURL,
    termsAndConditionsURL
} from '../../../../Constants/Constants'
import { requestJSON } from '../../../../../NonReact/common/API/REST/RESTClient'
import { APIElements, hideInsightsHub, showInsightsHub } from '../../../../../NonReact/constants/ApiConstants'
import { RESTMethods } from '../../../../../NonReact/constants/REST'
import { displayError } from '../../../../../React/Common/Utils/Messages'
import { currentUserState$ } from '../../../../../NonReact/common/RxJS/UserInfoState'
import { restartTour } from '../../../../../React/Common/Utils/TourStorage'
import { AvatarMenuItem } from '../../../../../React/Common/Types'
import { DarkModeType, useDarkMode } from '../../../../../React/Common/Hooks/useDarkMode'
import { useIsInsightsAvailable } from '../../../../Hooks/useIsInsightsAvailable'
import { ReportingAccessLevels } from '../../../../../NonReact/constants/Permissions'
import { isVarNotEmpty } from '../../../../../NonReact/utils/GlobalFunctions'
import { UserInfo, useUserInfo } from '../../../../Hooks/useUserInfo'
import { clearContentfulCache } from '../../../../../React/Common/API/Contentful/ContentfulAPI'
import { UserMenuListItem } from './UserMenuListItem'
import TimezoneAdapter from '../../../../../React/TimezoneModal/Adapter/TimezoneAdapter'
import { URLConstructor } from '../../../../../NonReact/utils/URLBuilder'
import { RouteID } from '../Types/Types'
import { SPACE } from '../../../../../React/Common/Constants/Separators'
import { useFeatureFlag } from '../../../../../React/Common/Hooks/useFeatureFlag'
import { ENABLE_NEW_USER_TOUR } from '../../../../../React/Common/Constants/FeatureFlags'
import { isNativeTeamsApp } from '../../../../../React/Common/Utils/TeamsNativeUtils'

type Props = {
    closeMenu: (openNotifications: boolean) => void
}

export const UserMenu: FC<Props> = ({ closeMenu }: Props): ReactElement => {
    const language: CLanguageHandler = useContext(LanguageContext) as CLanguageHandler
    language.loadBundle(UserMenuLanguage.NAME)

    const [isInsightsSubmitting, setIsInsightsSubmitting]: ReactState<boolean> = useState<boolean>(false)
    const [areHiddenOptionsShown, setAreHiddenOptionsShown]: ReactState<boolean> = useState<boolean>(false)

    const isMounted: MutableRefObject<boolean> = useIsMountedRef()

    const { darkModeAvatarMenu }: DarkModeType = useDarkMode()

    const branding: Nullable<Branding> = useBranding()

    const isInsightsAvailable: Definable<boolean> = useIsInsightsAvailable(true)

    const [userInfo]: UserInfo = useUserInfo()

    const isUserTourEnabled: Definable<boolean> = useFeatureFlag(ENABLE_NEW_USER_TOUR)

    const isTeamsApp: boolean = isNativeTeamsApp()

    useHotkeys('ctrl+shift+c', (): void => {
        if (isMounted.current) {
            setAreHiddenOptionsShown(true)
        }
    })

    const switchInsightsHub = useCallback((): void => {
        if (!isInsightsSubmitting) {
            setIsInsightsSubmitting(true)

            requestJSON(userInfo?.InsightsHubHidden ? showInsightsHub : hideInsightsHub, {
                method: RESTMethods.PUT
            })
                .catch((e: Error): void => {
                    displayError(e)
                })
                .finally((): void => {
                    setTimeout((): void => {
                        currentUserState$.resolver?.(true, displayError).finally((): void => {
                            if (isMounted.current) {
                                setIsInsightsSubmitting(false)
                            }
                        })
                    }, 100)
                })
        }
    }, [userInfo, isInsightsSubmitting])

    const avatarMenuItems: AvatarMenuItem[] = useMemo(
        (): AvatarMenuItem[] => [
            {
                icon: IconName.NOTIFICATION_ENABLED,
                label: language.get(UserMenuLanguage.UserMenu_Notifications),
                action: (): void => closeMenu(true)
            },
            ...(isUserTourEnabled
                ? [
                      {
                          icon: IconName.TOUR,
                          label: language.get(UserMenuLanguage.UserMenu_UserTour),
                          action: (): void => restartTour()
                      }
                  ]
                : []),
            ...(isInsightsAvailable
                ? [
                      userInfo?.InsightsHubHidden
                          ? {
                                icon: IconName.SHOW_INSIGHTS,
                                label: language.get(UserMenuLanguage.UserMenu_ShowInsights),
                                action: switchInsightsHub
                            }
                          : {
                                icon: IconName.HIDE_INSIGHTS,
                                label: language.get(UserMenuLanguage.UserMenu_HideInsights),
                                action: switchInsightsHub
                            }
                  ]
                : []),
            ...(isTeamsApp || userInfo?.SessionInfo?.SignedInWithSSO
                ? []
                : [
                      {
                          icon: IconName.CHANGE_PASSWORD,
                          label: language.get(UserMenuLanguage.UserMenu_ChangePassword),
                          action: (): void => moveToURL(getChangePasswordURL())
                      }
                  ]),
            {
                icon: IconName.CHANGE_LANGUAGE,
                label: language.get(UserMenuLanguage.UserMenu_ChangeLanguage),
                action: (): void => moveToURL(getChangeLanguageURL())
            },
            {
                icon: IconName.TIMEZONE,
                label: language.get(UserMenuLanguage.UserMenu_Timezone),
                action: (): TimezoneAdapter => {
                    closeMenu(false)
                    return TimezoneAdapter.newInstance()
                },
                className: 'um-timezone'
            },
            darkModeAvatarMenu,
            {
                icon: IconName.HELP_AVATAR_MENU,
                label: language.get(UserMenuLanguage.UserMenu_Help),
                action: openHelp
            },
            {
                icon: IconName.DOWNLOAD,
                label: language.get(UserMenuLanguage.UserMenu_Resources),
                subMenu: [
                    {
                        label: language.get(UserMenuLanguage.UserMenu_Resources_Terms),
                        action: (): void => moveToURL(termsAndConditionsURL())
                    },
                    {
                        label: language.get(UserMenuLanguage.UserMenu_Resources_CookiePolicy),
                        action: (): void => moveToURL(cookiePolicyURL())
                    },
                    {
                        label: language.get(UserMenuLanguage.UserMenu_Resources_PrivacyPolicy),
                        action: (): void => moveToURL(privacyPolicyURL())
                    }
                ]
            },
            ...(areHiddenOptionsShown
                ? [
                      {
                          icon: IconName.WRENCH,
                          label: language.get(UserMenuLanguage.UserMenu_ChangeBrowserProfile),
                          action: (): void =>
                              moveToURL(
                                  URLConstructor.newInstance({
                                      pathParams: [APIElements.CCS, APIElements.APP_V2, RouteID.CHANGE_USER_AGENT]
                                  })
                              )
                      },
                      {
                          icon: IconName.WRENCH,
                          label: language.get(UserMenuLanguage.UserMenu_ClearContentful),
                          action: (): void => clearContentfulCache()
                      }
                  ]
                : []),
            ...(isTeamsApp
                ? []
                : [
                      {
                          icon: IconName.LOGOUT,
                          label: language.get(UserMenuLanguage.UserMenu_Logout),
                          action: (): void => signOut((): void => moveToURL(getAuthURL()))
                      }
                  ])
        ],
        [userInfo, branding, switchInsightsHub, darkModeAvatarMenu]
    )

    const getReportingAccessEntry = (): ReactElement => {
        if (
            isVarNotEmpty(userInfo?.Scope?.ReportingAccess) &&
            (userInfo?.Scope?.ReportingAccess as ReportingAccessLevels) !== ReportingAccessLevels.NONE
        ) {
            return <div className='um-licence-type'>{userInfo?.Scope?.ReportingAccess}</div>
        }

        return <></>
    }

    return (
        <div className='user-menu'>
            <div className='um-user-badge-container'>
                <UserBadge name={userInfo?.FullName ?? EMPTY_STRING} />
                <div className='um-user-info'>
                    <div className='um-user-name'>
                        {userInfo?.FullName?.split(SPACE)?.map(
                            (name: string): ReactElement => <span key={name}>{name}</span>
                        )}
                    </div>
                    {getReportingAccessEntry()}
                </div>
            </div>
            <div className='um-list-area'>
                <List className='um-list'>
                    {avatarMenuItems.map(
                        (item: AvatarMenuItem): ReactElement => (
                            <UserMenuListItem key={item.label} item={item} />
                        )
                    )}
                </List>
            </div>
        </div>
    )
}
